import React, { useMemo } from 'react';
import { Plus, X } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Button } from '@cotera/client/v0/components/ui/button';
import { TokenInput } from '../tiptap/token-input';
import type { ToolInputRefIssue } from '../tool-input-ref-issues';
import { RefIssueNotice } from '../ref-issue-notice';
import type { ToolInputControlProps } from './index';
import {
  AppendDraftOps,
  readAppendDraft,
  serializeAppendDraft,
  type AppendDraft,
} from './bigquery-append-model';

/**
 * Which cell a bad ref sits in. Issues are reported against a dotted path into
 * the saved param — `rows.<rowIndex>.<colIndex>` for a cell (see
 * `collectTemplateRefs`) — and this turns one back into the cell that drew it.
 */
const CELL_ISSUE_RE = /^rows\.(\d+)\.(\d+)$/;

const cellOfIssue = (
  issue: ToolInputRefIssue
): { row: number; col: number } | null => {
  const match = CELL_ISSUE_RE.exec(issue.path);
  if (match === null) {
    return null;
  }
  return { row: Number(match[1]), col: Number(match[2]) };
};

/**
 * Builds the `columns` + `rows` a `bigquery-sql/append` node sends.
 *
 * The generic editor sees a `string[]` and an `any[][]` and renders two JSON
 * textareas, which makes the author line up positional cells with columns by
 * hand. This draws the two as one grid: name the columns, then fill a row of
 * cells per record. Each cell is the same token input every other tool input
 * uses, so a value can be typed literally or pulled from an earlier step with
 * `{{ref}}` — and either way it reaches the tool as data (BigQuery's streaming
 * insert), never spliced into SQL.
 */
export const BigQueryAppendControl: React.FC<ToolInputControlProps> = ({
  params,
  sources,
  refIssues,
  setValues,
}) => {
  const draft = useMemo(() => readAppendDraft(params), [params]);

  const commit = (next: AppendDraft): void => {
    setValues(serializeAppendDraft(next));
  };

  const { issuesByCell, otherIssues } = useMemo(() => {
    const byCell = new Map<string, ToolInputRefIssue[]>();
    const leftover: ToolInputRefIssue[] = [];
    for (const issue of refIssues) {
      const cell = cellOfIssue(issue);
      if (
        cell === null ||
        cell.row >= draft.rows.length ||
        cell.col >= draft.columns.length
      ) {
        leftover.push(issue);
        continue;
      }
      const key = `${cell.row}.${cell.col}`;
      const existing = byCell.get(key);
      if (existing === undefined) {
        byCell.set(key, [issue]);
      } else {
        existing.push(issue);
      }
    }
    return { issuesByCell: byCell, otherIssues: leftover };
  }, [refIssues, draft.rows.length, draft.columns.length]);

  const hasColumns = draft.columns.length > 0;

  return (
    <div className="flex flex-col gap-4">
      {/* Columns */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-0.5">
          <Text.Caption className="text-foreground font-medium">
            Columns
          </Text.Caption>
          <Text.Caption className="text-muted-foreground">
            The columns each row writes into, in order. Names must match the
            BigQuery table.
          </Text.Caption>
        </div>
        {hasColumns ? (
          draft.columns.map((column, colIndex) => (
            <div key={colIndex} className="flex items-center gap-2">
              <Input
                className="h-8 w-full font-mono text-xs"
                placeholder="column_name"
                spellCheck={false}
                value={column}
                onChange={(event) =>
                  commit(
                    AppendDraftOps.renameColumn(
                      draft,
                      colIndex,
                      event.target.value
                    )
                  )
                }
              />
              <button
                type="button"
                onClick={() =>
                  commit(AppendDraftOps.removeColumn(draft, colIndex))
                }
                title="Remove column"
                aria-label={`Remove column ${colIndex + 1}`}
                className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-6 shrink-0 items-center justify-center rounded transition-colors"
              >
                <X className="size-3.5" />
              </button>
            </div>
          ))
        ) : (
          <Text.Caption className="text-muted-foreground">
            No columns yet.
          </Text.Caption>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 self-start"
          onClick={() => commit(AppendDraftOps.addColumn(draft))}
        >
          <Plus className="mr-1 size-3.5" />
          Add a column
        </Button>
      </div>

      {/* Rows */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-0.5">
          <Text.Caption className="text-foreground font-medium">
            Rows
          </Text.Caption>
          <Text.Caption className="text-muted-foreground">
            Each row is one record to insert. Type a value, or press{' '}
            <span className="font-mono">/</span> to insert data from a previous
            step.
          </Text.Caption>
        </div>

        {!hasColumns ? (
          <Text.Caption className="text-muted-foreground">
            Add a column before adding rows.
          </Text.Caption>
        ) : draft.rows.length === 0 ? (
          <Text.Caption className="text-muted-foreground">
            No rows yet.
          </Text.Caption>
        ) : (
          draft.rows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className="border-border/60 flex flex-col gap-2 rounded-md border p-2"
            >
              <div className="flex items-center justify-between gap-2">
                <Text.Caption className="text-muted-foreground">
                  Row {rowIndex + 1}
                </Text.Caption>
                <button
                  type="button"
                  onClick={() =>
                    commit(AppendDraftOps.removeRow(draft, rowIndex))
                  }
                  title="Remove row"
                  aria-label={`Remove row ${rowIndex + 1}`}
                  className="text-muted-foreground hover:bg-muted hover:text-foreground -mr-1 flex size-6 shrink-0 items-center justify-center rounded transition-colors"
                >
                  <X className="size-3.5" />
                </button>
              </div>
              {draft.columns.map((column, colIndex) => (
                <div key={colIndex} className="flex flex-col gap-1">
                  <Text.Caption className="text-muted-foreground truncate font-mono text-[11px]">
                    {column.trim() === '' ? `column ${colIndex + 1}` : column}
                  </Text.Caption>
                  <TokenInput
                    value={row[colIndex] ?? ''}
                    sources={sources}
                    onChange={(template) =>
                      commit(
                        AppendDraftOps.setCell(
                          draft,
                          rowIndex,
                          colIndex,
                          template
                        )
                      )
                    }
                  />
                  <RefIssueNotice
                    issues={issuesByCell.get(`${rowIndex}.${colIndex}`) ?? []}
                  />
                </div>
              ))}
            </div>
          ))
        )}

        {hasColumns ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 self-start"
            onClick={() => commit(AppendDraftOps.addRow(draft))}
          >
            <Plus className="mr-1 size-3.5" />
            Add a row
          </Button>
        ) : null}
      </div>

      <RefIssueNotice issues={otherIssues} />
    </div>
  );
};
